import { fabric } from "fabric";
import { v4 } from "uuid";

import { CanvasHandler } from "../../handlers";
import { FabricEvent } from "../../types/FabricCustomType";
import { IObjectDesingner } from "../../types/HandlerObjectType";
import store from "../../redux/store";

type FabricSvgGroup = fabric.Group & {
  id?: string;
  container?: string;
  editable?: boolean;
  superType?: string;
  svg?: string;
  loadType?: string;
};
class Icon implements IObjectDesingner {
  private canvasHandler: CanvasHandler;
  constructor(canvasHandler: CanvasHandler) {
    this.canvasHandler = canvasHandler;
  }
  onRotated(event: FabricEvent) {
    throw new Error("Method not implemented.");
  }

  onMouseDblClick(event: FabricEvent<Event>) {
    return;
  }

  public onInit() {
    this.canvasHandler.interactionHandler.drawing("svg");
    this.canvasHandler.activeShape = null;
  }

  public onMousedown(event: FabricEvent<MouseEvent>) {
    const { x, y } = this.canvasHandler.canvas.getPointer(event.e);
    this.createIcon(x, y);
  }

  public onMousemove(event: FabricEvent<MouseEvent>) {
    return;
  }

  public onMouseup(event: FabricEvent<MouseEvent>) {
    return;
  }

  public updateProperties(
    selectedItem: any,
    key: string,
    value: any,
    allValues: any
  ) {
    if (key === "iconType") {
      const editable = this.canvasHandler.editable;
      const containerId = this.canvasHandler.container.id;

      const prevProps = {
        id: selectedItem.id,
        left: selectedItem.left,
        top: selectedItem.top,
        scaleX: selectedItem.scaleX ?? 1,
        scaleY: selectedItem.scaleY ?? 1,
        angle: selectedItem.angle ?? 0,
        flipX: selectedItem.flipX ?? false,
        flipY: selectedItem.flipY ?? false,
        originX: selectedItem.originX ?? "center",
        originY: selectedItem.originY ?? "center",
        opacity: selectedItem.opacity ?? 1,
        fill: selectedItem.fill,
        hasControls: selectedItem.hasControls,
        hasBorders: selectedItem.hasBorders,
        selectable: selectedItem.selectable,
        lockMovementX: selectedItem.lockMovementX,
        lockMovementY: selectedItem.lockMovementY,
        hoverCursor: selectedItem.hoverCursor,
        name: value.id ?? "New icon SVG",
      };

      const svgConverted = document
        .createRange()
        .createContextualFragment(value.svg);
      const serializer = new XMLSerializer();
      const svgStr = serializer.serializeToString(svgConverted);

      fabric.loadSVGFromString(svgStr, (objects, options) => {
        const newIcon = fabric.util.groupSVGElements(
          objects,
          options
        ) as FabricSvgGroup;

        newIcon.set({
          left: prevProps.left,
          top: prevProps.top,
          scaleX: prevProps.scaleX,
          scaleY: prevProps.scaleY,
          angle: prevProps.angle,
          flipX: prevProps.flipX,
          flipY: prevProps.flipY,
          originX: prevProps.originX,
          originY: prevProps.originY,
          opacity: prevProps.opacity,
          hasControls: prevProps.hasControls ?? editable,
          hasBorders: prevProps.hasBorders ?? editable,
          selectable: prevProps.selectable ?? editable,
          lockMovementX: prevProps.lockMovementX ?? !editable,
          lockMovementY: prevProps.lockMovementY ?? !editable,
          hoverCursor: prevProps.hoverCursor ?? (!editable ? "pointer" : "move"),
          objectCaching: !editable,
          fill: prevProps.fill ,
        });

        newIcon.id = v4();
        newIcon.container = containerId;
        newIcon.editable = editable;
        newIcon.superType = "svg";
        newIcon.type = "svg" as any;
        newIcon.name = prevProps.name;
        newIcon.svg = value.svg;
        newIcon.loadType = "svg";

        newIcon._objects.forEach((path: any) => {
          path.set("fill", prevProps.fill);
          path.setCoords();
        });

        newIcon.setCoords();

        this.canvasHandler.canvas.remove(selectedItem);
        this.canvasHandler.canvas.add(newIcon);
        this.canvasHandler.canvas.setActiveObject(newIcon);
        this.canvasHandler.canvas.requestRenderAll();
        this.canvasHandler.transactionHandler.save("add");

        const { onModified } = this.canvasHandler;
        if (onModified) onModified(newIcon);
      });

      return;
    }

    if (key === "fill") {
      selectedItem.set({
        fill: value
      })
      selectedItem._objects.forEach((path: any) => {
        path.set(key, value);
        path.setCoords();
      });
      selectedItem.addWithUpdate();
    }

    if (key === "scaleX") {
      selectedItem.set({
        originX: "center",
        originY: "center",
        centeredScaling: true,
        scaleX: value,
        scaleY: value,
      });
    }
    this.canvasHandler.canvas.requestRenderAll();
    const { onModified } = this.canvasHandler;
    if (onModified) onModified(selectedItem);
  }

  private createIcon(x: number, y: number) {
    const state = store.getState();
    const {
      properties: { svgIcon, svgFill, svgScale },
    } = state;
    this.canvasHandler.isDown = true;
    const editable = this.canvasHandler.editable;
    const id = v4();

    const svgEl = svgIcon.svg;
    const svgConverted = document.createRange().createContextualFragment(svgEl);
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(svgConverted);

    const containerId = this.canvasHandler.container.id;

    fabric.loadSVGFromString(svgStr, (objects, options) => {
      const loadedObject = fabric.util.groupSVGElements(objects, options) as FabricSvgGroup;

      loadedObject.set({
        id,
        hasControls: editable,
        hasBorders: editable,
        selectable: editable,
        lockMovementX: !editable,
        lockMovementY: !editable,
        hoverCursor: !editable ? "pointer" : "move",
        container: containerId,
        editable,
        left: x,
        top: y,
        scaleX: svgScale,
        scaleY: svgScale,
        originX: "center",
        originY: "center",
        opacity: 1,
        objectCaching: !editable,
        superType: "svg",
        type: "svg",
        name: svgIcon.id,
        svg: svgEl,
        fill: svgFill,
        loadType: "svg",
      });

      loadedObject._objects.forEach((path: any) => {
        path.set("fill", svgFill);
        path.setCoords();
      });

      loadedObject?.setControlsVisibility({
        tl: false, //top-left
        mt: false, // middle-top
        tr: false, //top-right
        ml: false, //middle-left
        mr: false, //middle-right
        bl: false, // bottom-left
        mb: false, //middle-bottom
        br: false, //bottom-right
        mtr: true,
      });

      this.canvasHandler.canvas.add(loadedObject);
      this.canvasHandler.activeShape = loadedObject;
      this.generate(false, false);
      this.finish();
    });
  }

  private generate(isDown: any, loaded: any) {
    const { editable, gridOption, onAdd } = this.canvasHandler;
    const obj = this.canvasHandler.activeShape;
    if (!this.canvasHandler.transactionHandler.active && !loaded)
      this.canvasHandler.transactionHandler.save("add");
    if (onAdd && editable && !loaded) onAdd(obj);

    this.canvasHandler.isDown = isDown;
    this.canvasHandler.activeShape = null;
  }

  private finish() {
    this.canvasHandler.activeShape = null;
    this.canvasHandler.canvas.discardActiveObject();
    this.canvasHandler.canvas.renderAll();
  }
}
export default Icon;
